import type { Dayjs } from "dayjs"
import type { ReactNode } from "react"
import classNames from "classnames"
import {
  ReloadOutlined,
  SearchOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Table,
  Tag,
  Typography,
} from "antd"
import type { TableColumnsType } from "antd"
import { formatDateTime, formatSeconds, optionalValue } from "~/common/utils"
import { ResourcePreview } from "~/components/ResourcePreview"
import { usePagedQuery, type PagedResponse } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { RangePicker } = DatePicker
const { Paragraph, Text, Title } = Typography

// 画质提升 / 去字幕等视频处理任务的 Staff 列表骨架：筛选项、通用列和分页一致

type VideoTaskFilterForm = {
  createdRange?: [Dayjs, Dayjs]
  taskId?: string
  userId?: string
}

type VideoTaskFilters = {
  task_id?: string
  user_id?: string
  created_from?: string
  created_to?: string
}

type StaffVideoTaskItem = {
  id: string
  user_id: string
  created_t?: string | number | null
  status: 1 | 2 | 3 | 4 | 5
  input_duration?: number | null
  points: number
  video_url?: string | null
  output_video_url?: string | null
  provider: string
  model: string
  download_count?: number | null
  error_message?: string | null
}

const renderId = (value?: string | null) => {
  if (!value) return "-"

  return (
    <Paragraph
      className={styles.copyableText}
      copyable={{ text: value, tooltips: ["复制", "已复制"] }}
      ellipsis={{ tooltip: value }}
    >
      {value}
    </Paragraph>
  )
}

const renderVideo = (videoUrl?: string | null) => {
  if (!videoUrl) return "-"

  return (
    <ResourcePreview
      size="small"
      src={videoUrl}
      type="video"
    />
  )
}

type VideoTaskColumnsConfig<TItem> = {
  // 状态 4 / 5 的文案，例如「增强完成」「增强失败」
  doneLabel: string
  failedLabel: string
  resultTitle: string
  // 插在「状态」列之后的业务专属列
  settingColumns: TableColumnsType<TItem>
}

export const getVideoTaskColumns = <TItem extends StaffVideoTaskItem>({
  doneLabel,
  failedLabel,
  resultTitle,
  settingColumns,
}: VideoTaskColumnsConfig<TItem>): TableColumnsType<TItem> => {
  const statusConfig: Record<
    StaffVideoTaskItem["status"],
    { color: string; label: string }
  > = {
    1: { color: "default", label: "等待提交" },
    2: { color: "blue", label: "已提交" },
    3: { color: "processing", label: "处理中" },
    4: { color: "success", label: doneLabel },
    5: { color: "error", label: failedLabel },
  }

  return [
    {
      title: "创建时间",
      dataIndex: "created_t",
      fixed: "left",
      width: 180,
      render: formatDateTime,
    },
    {
      title: "用户 ID",
      dataIndex: "user_id",
      fixed: "left",
      width: 220,
      render: renderId,
    },
    {
      title: "任务 ID",
      dataIndex: "id",
      width: 220,
      render: renderId,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 120,
      render: (status: StaffVideoTaskItem["status"]) => {
        const config = statusConfig[status]
        return <Tag color={config.color}>{config.label}</Tag>
      },
    },
    ...settingColumns,
    {
      title: "输入时长",
      dataIndex: "input_duration",
      width: 130,
      render: formatSeconds,
    },
    {
      title: "消耗积点",
      dataIndex: "points",
      align: "center",
      width: 110,
      render: (points: number) => `${points} 积点`,
    },
    {
      title: "原视频",
      dataIndex: "video_url",
      width: 110,
      render: renderVideo,
    },
    {
      title: resultTitle,
      dataIndex: "output_video_url",
      width: 110,
      render: renderVideo,
    },
    {
      title: "供应商 / 模型",
      width: 220,
      render: (_, record) => (
        <Flex vertical>
          <Text>{record.provider}</Text>
          <Text type="secondary">{record.model}</Text>
        </Flex>
      ),
    },
    {
      title: "下载次数",
      dataIndex: "download_count",
      align: "center",
      width: 100,
    },
    {
      title: "错误信息",
      dataIndex: "error_message",
      width: 280,
      render: (errorMessage: string) =>
        errorMessage ? (
          <Text
            ellipsis={{ tooltip: errorMessage }}
            type="danger"
          >
            {errorMessage}
          </Text>
        ) : (
          "-"
        ),
    },
  ]
}

type VideoTaskListPageProps<TItem> = {
  title: string
  description: ReactNode
  columns: TableColumnsType<TItem>
  fetchPage: (
    params: VideoTaskFilters & { page: number; page_size: number },
    signal: AbortSignal,
  ) => Promise<PagedResponse<TItem>>
  errorMessage: string
  defaultPageSize?: number
  pageSizeOptions?: number[]
}

export const VideoTaskListPage = <TItem extends StaffVideoTaskItem>({
  title,
  description,
  columns,
  fetchPage,
  errorMessage,
  defaultPageSize,
  pageSizeOptions,
}: VideoTaskListPageProps<TItem>) => {
  const [form] = Form.useForm<VideoTaskFilterForm>()
  const { total, loading, search, refresh, tableProps } = usePagedQuery<
    VideoTaskFilters,
    TItem
  >({
    fetchPage,
    initialFilters: {},
    defaultPageSize,
    pageSizeOptions,
    errorMessage,
  })

  const applyFilters = async () => {
    const values = await form.validateFields()
    const createdRange = values.createdRange

    search({
      task_id: optionalValue(values.taskId),
      user_id: optionalValue(values.userId),
      created_from: createdRange?.[0].toISOString(),
      created_to: createdRange?.[1].toISOString(),
    })
  }

  const resetFilters = () => {
    form.resetFields()
    search({})
  }

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>{title}</Title>
        <Text type="secondary">{description}</Text>
      </Flex>

      <section className={styles.filterPanel}>
        <Form<VideoTaskFilterForm>
          form={form}
          layout="vertical"
          onFinish={applyFilters}
        >
          <Flex
            align="flex-end"
            gap={16}
            wrap
          >
            <Form.Item
              className={styles.filterField}
              label="任务 ID"
              name="taskId"
            >
              <Input
                allowClear
                placeholder="请输入任务 ID（精准匹配）"
              />
            </Form.Item>
            <Form.Item
              className={styles.filterField}
              label="用户 ID"
              name="userId"
            >
              <Input
                allowClear
                placeholder="请输入用户 ID（精准匹配）"
              />
            </Form.Item>
            <Form.Item
              className={classNames(styles.filterField, styles.dateRange)}
              label="任务创建时间"
              name="createdRange"
            >
              <RangePicker
                allowClear
                format="YYYY-MM-DD HH:mm"
                placeholder={["开始时间", "结束时间"]}
                showTime={{ format: "HH:mm" }}
              />
            </Form.Item>
            <Form.Item
              className={classNames(styles.filterField, styles.action)}
            >
              <Flex gap={10}>
                <Button onClick={resetFilters}>重置</Button>
                <Button
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                  type="primary"
                >
                  查询
                </Button>
              </Flex>
            </Form.Item>
          </Flex>
        </Form>
      </section>

      <section className={styles.listPanel}>
        <Flex
          className={styles.listHeader}
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <VideoCameraOutlined className={styles.sectionIcon} />
            <Title
              className={styles.listTitle}
              level={4}
            >
              {title}记录
            </Title>
            <Text type="secondary">共 {total} 条</Text>
          </Flex>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={refresh}
          >
            刷新
          </Button>
        </Flex>
        <Table<TItem>
          columns={columns}
          {...tableProps}
          rowKey={(record) => record.id}
          scroll={{ x: "max-content" }}
        />
      </section>
    </Flex>
  )
}
